import { action, observable } from 'mobx';
import { FormulaFormVM } from '../FormulaComponents/template/FormulaFormVM';
import { HydraulicFormulaController } from 'cd-eft/services/eftSetApi';
import { IFormulaProps, formulaList1, formulaList2 } from './Formula.config';
import { formulaLabelsConfig } from 'cd-eft/components/EFH2/Formulas/Formula/Formula.labels.config';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import stores from 'cd-eft/models/Stores';
import { formatRound, formatRoundS } from 'cd-eft/utils/mathUtilities';

interface ITab {
  id: string;
  name: string;
  label: string;
  className: string;
  checked: boolean;
  onChange: (e: any) => void;
}

export class FormulaVM extends FormulaFormVM {
  @observable formulaProps: IFormulaProps = { name: '', path: '', id: 0, section: '', compute: '' };
  @observable formData: any = {};
  @observable tabs: ITab[] = [];
  @observable activeTab: string = '';
  @observable results: any = {};
  @observable inputsCopyForPrint: any = {};
  @observable formValidation: any = { hasError: false };
  @observable errorMessages: any = {};

  private formatObjectOfObjects(entries: [string, any][]) {
    for (const [key, value] of entries) {
      if (typeof value === "object") {
        for (const [innerKey, innerValue] of Object.entries(value)) {
          if (typeof innerValue === "number") {
            let decimalPlaces: number = 2;
            if (formulaLabelsConfig[innerKey]?.decimalPlaces) {
              decimalPlaces = formulaLabelsConfig[innerKey].decimalPlaces;
            }
            value[innerKey] = formatRoundS(innerValue, decimalPlaces);
          }
        }
      }
    }
    return entries;
  }

  @action.bound init = async (formulaId: string) => {
    let formulaProps = formulaList1.find((el) => el.path === formulaId);
    if (!formulaProps && stores.AppStore.userProfileStore.applicationFlags.enableformulas1) formulaProps = formulaList2.find((el) => el.path === formulaId);

    if (formulaProps) {
      this.formulaProps = formulaProps;

      await this.getFormData(`${formulaProps.section}_${formulaProps.name}_${formulaProps.id}`);
      if (this.defaultValues) {
        this.tabs = [];
        Object.keys(this.defaultValues).forEach((key) => {
          if (this.defaultValues[key] instanceof Object) {
            // check for tabs and fill tabs
            if (formulaLabelsConfig[key] && formulaLabelsConfig[key].tab) {
              this.addTab(key);
            }

            Object.keys(this.defaultValues[key]).forEach((k) => {
              // set validation for each element
              this.formValidation[k] = false;

              // get Error Message for nested formula
              if (formulaLabelsConfig[k] && formulaLabelsConfig[k].errorCode)
                this.addErrorMsgForField(k, formulaLabelsConfig[k].errorCode);

              if (formulaLabelsConfig[k] && formulaLabelsConfig[k].disabled) {
                const inputElement = document.getElementById(k) as HTMLInputElement;
                if (inputElement) {
                  inputElement.disabled = formulaLabelsConfig[k].disabled;
                }
              }

              if (formulaLabelsConfig[k] && formulaLabelsConfig[k].checked) {
                const inputElement = document.getElementById(k) as HTMLInputElement;
                if (inputElement) {
                  inputElement.checked = formulaLabelsConfig[k].checked;
                }
              }
            });
          } else {
            // set validation for each element
            this.formValidation[key] = false;

            // get Error Message for flat formula
            if (formulaLabelsConfig[key] && formulaLabelsConfig[key].errorCode)
              this.addErrorMsgForField(key, formulaLabelsConfig[key].errorCode);
          }
        });

        // if (this.tabs.length > 0) {
        if (this.tabs.length > 0) {
          this.formData = this.defaultValues[this.activeTab];
        } else {
          this.formData = this.defaultValues;
        }
        const entries = Object.entries(this.defaultValues)
        this.formatObjectOfObjects(entries);
        entries.forEach(([key, value]) => {
          let decimalPlaces: number = 2;
          if (formulaLabelsConfig[key].decimalPlaces) {
            decimalPlaces = formulaLabelsConfig[key].decimalPlaces;
          }

          if (typeof (value) === 'number') {
            this.defaultValues[key] = this.roundingHelperString(this.defaultValues[key], decimalPlaces)
          }
        });
      }
      // get Common Error Message
      this.hasResults = false;
      this.errorMessages.common = await MessageCenter.addErrorLabelFromCode('Common_26');
    }
  };

  @action.bound addErrorMsgForField = async (field, code) => {
    const msg = await MessageCenter.addErrorLabelFromCode(code);
    this.errorMessages[field] =
      code === 'Common_10' ? formulaLabelsConfig[field].label.split('(')[0].trim() + ' ' + msg : msg;
  };

  @action.bound initializeDefaultValues(): void {
    this.formData = this.defaultValues;
  }

  @action.bound addTab = (tab: string) => {
    if (this.tabs.length === 0) this.activeTab = tab;
    this.tabs.push({
      id: tab,
      name: tab,
      label: formulaLabelsConfig[tab].label,
      className: 'usa-radio__input--tile',
      checked: this.tabs.length === 0 ? true : false,
      onChange: this.onChangeTabButton,
    });
  };

  @action.bound onChangeTabButton = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.activeTab = e.target.name;
    this.tabs.forEach((el) => (el.checked = el.id === e.target.name ? true : false));
    this.formData = this.defaultValues[this.activeTab];
    this.formValidation = { hasError: false };
    this.validateForm();
  };

  // @action.bound handleOnKeyDown(event) {
  //   ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
  // }

  @action.bound handleInputChange(e: React.ChangeEvent<HTMLInputElement>, section?: string) {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      this.setValueFromInput(name, checked, section);
      return;
    }
    if (type !== 'checkbox') {
      if (value.length > 7) {
        return
      }
      this.setValueFromInput(name, value === '' ? '' : value, section);
    }
  }

  @action.bound handleInputFocus = (e: React.FocusEvent<HTMLInputElement>, section?: string) => {
    const { name, value } = e.target;

    // Rounds to 2 or customizd decimal places if the value is a valid number
    let inputValue: number | string = parseFloat(value);
    let decimalPlaces: number = 2;

    if (formulaLabelsConfig[name].decimalPlaces) {
      decimalPlaces = formulaLabelsConfig[name].decimalPlaces;
    }

    if (isNaN(inputValue) || value === '') {
      inputValue = ""
      this.setValueFromInput(name, 0, section) //manually changed to 0 and '' so that the error gets triggered on ''
      this.setValueFromInput(name, '', section)
    } else {
      inputValue = this.roundingHelperNumber(inputValue, decimalPlaces);
    }

    if (typeof inputValue === "number") this.setValueFromInput(name, this.roundingHelperString(inputValue, decimalPlaces), section);

    this.validateField(name, inputValue);

    if (this.formValidation[name]) {
      MessageCenter.clearMessages();

      if (this.errorMessages[name]) {
        MessageCenter.addNotification(
          this.errorMessages[name],
          'Error',
          formulaLabelsConfig[name] && formulaLabelsConfig[name].errorCode
        );
      } else {
        this.hasResults = false;
        MessageCenter.addNotification(this.errorMessages.common, 'Error', 'Common_26');
      }

    }

  };


  @action.bound validateField = (name, value) => {
    const configData = formulaLabelsConfig[name];
    if (configData) {
      if ((value === '' || Number(value) <= 0 || value === null) && configData.required) {
        this.formValidation[name] = true;
        this.formValidation.hasError = true;
        if (MessageCenter.currentMessage) MessageCenter.clearMessages();
        if (configData.errorCode) {
          MessageCenter.addNotification(this.errorMessages[name], 'Error', configData.errorCode);
        } else {
          MessageCenter.addNotification(this.errorMessages.common, 'Error', 'Common_26');
        }
      } else if (
        (!configData.min && !configData.max) ||
        (configData.min && !configData.max && value >= configData.min) ||
        (!configData.min && configData.max && value <= configData.max) ||
        (configData.min && configData.max && value >= configData.min && value <= configData.max)
      ) {
        this.formValidation[name] = false;
        MessageCenter.clearMessages();
        // check for other invalid fields
        if (Object.values(this.formValidation).filter((el) => el === true).length <= 1) {
          this.formValidation.hasError = false;
        } else {
          this.hasResults = false;
          this.formValidation.hasError = true;
          // MessageCenter.addNotification(this.errorMessages.common, 'Error', 'Common_26');
        }
      } else {
        this.formValidation[name] = true;
        this.formValidation.hasError = true;
        this.hasResults = false;
        if (configData.errorCode) {
          if (MessageCenter.currentMessage) MessageCenter.clearMessages();
          MessageCenter.addNotification(this.errorMessages[name], 'Error', configData.errorCode);
        }
      }
    }
  };

  @action.bound validateForm = () => {
    Object.keys(this.formData).forEach((key) => {
      if (this.formData[key] instanceof Object) {
        Object.keys(this.formData[key]).forEach((k) => this.validateField(k, this.formData[key][k]));
      } else {
        this.validateField(key, this.formData[key]);
      }
    });
  };

  @action.bound setValueFromInput = (name: string, value: string | number | boolean, section?: string | undefined) => {
    if (section) {
      this.formData[section][name] = value;
      if (typeof value === 'boolean' && formulaLabelsConfig[name].checkbox && formulaLabelsConfig[name].correspondingField) {
        const checkboxElement = document.getElementById(name) as HTMLInputElement;
        if (checkboxElement) {
          checkboxElement.checked = value;
        }
        const inputElement = document.getElementById(formulaLabelsConfig[name].correspondingField) as HTMLInputElement;
        if (inputElement) {
          inputElement.disabled = !value;
        }
      }
    } else {
      if (typeof value === 'number') {
        this.formData[name] = this.roundingHelperString(Number(value), 2);
      }
      else {
        this.formData[name] = value;
      }

    }
  };


  @action.bound onClickComputeFormula = async () => {
    this.isLoading = true;
    this.validateForm();
    if (!this.formValidation.hasError) {
      let data = undefined;
      if (typeof this.formulaProps.compute === 'function') {
        data = this.formulaProps.compute(this.formData);
      }
      else if (typeof this.formulaProps.compute === 'string') {
        if (this.formulaProps.compute === "calculateFrustrumPyramidUsingPOST") {
          this.formData = {
            nOfSides: parseInt(this.formData.nOfSides),
            a: parseInt(this.formData.a),
            b: parseInt(this.formData.b),
            height: parseInt(this.formData.height),
          }
        }
        data = await computeFormula(
          (val: boolean) => (this.disableComputeBtn = val),
          () => HydraulicFormulaController[this.formulaProps.compute as string](this.formData),
          (val: boolean) => (this.isLoading = val)
        );
      }
      if (data) {
        if ((data as any).validationPass === false) {
          this.hasResults = false;
          this.results = {};
          this.resetPlotData();
        }
        else {
          if ((data as any).stageDischargeList) {
            data = this.removeAndSetPlotData(data);
          }
          this.results = data;
          this.hasResults = true;
          this.inputsCopyForPrint = { ...this.formData };
        }
      }
    } else {
      this.hasResults = false;
      this.resetPlotData();
      MessageCenter.clearMessages();
      MessageCenter.addNotification(this.errorMessages.common, 'Error', 'Common_26');
    }
    this.isLoading = false;
  };

  makeUnitsGeneric(key) {
    if (key.specifyFor !== undefined && key.specifyFor.includes(this.formulaProps.name)) {
      return (key.label as string).replace(key.replace, key.specificUnits).trim();
    }
    else return key.label;
  }

  @action.bound launchAndExport() {
    // disable print button
    this.disablePrintBtn = true;
    this.exportedInputs = [];
    this.exportedResults = [];
    Object.keys(this.inputsCopyForPrint).forEach((input: string) => {
      if (this.inputsCopyForPrint[input] instanceof Object) {
        Object.keys(this.inputsCopyForPrint[input]).forEach((el) => {
          if (formulaLabelsConfig[el].label === 'Enable Tailwater' && !formulaLabelsConfig[el].hidden) {
            this.exportedInputs.push({
              label: formulaLabelsConfig[input][el] ? formulaLabelsConfig[input][el].label : el,
              value: this.inputsCopyForPrint[input][el] ? "Checked" : "Unchecked",
            });
          }
          else if (!formulaLabelsConfig[el].hidden) {
            this.exportedInputs.push({
              label: formulaLabelsConfig[el] ? this.makeUnitsGeneric(formulaLabelsConfig[el]) : el,
              value: this.inputsCopyForPrint[input][el],
            });
          }
        });
      } else {
        if (!formulaLabelsConfig[input].hidden) {
          this.exportedInputs.push({
            label: formulaLabelsConfig[input] ? this.makeUnitsGeneric(formulaLabelsConfig[input]) : input,
            value: this.inputsCopyForPrint[input],
          });
        }
      }
    });

    for (const result in this.results) {
      if (!['errorResponse', 'validationPass'].includes(result)) {
        const value = this.results[result];
        this.exportedResults.push({
          label: formulaLabelsConfig[result] ? this.makeUnitsGeneric(formulaLabelsConfig[result]) : result,
          value: (value ?
            (typeof value === 'number' && !isNaN(value)
              ? this.roundingHelperString(value, 2)
              : value
            )
            : '- - -'
          )
        });
      }
    }

    this.printFormula();
    this.disablePrintBtn = false;
  }
}

export const computeFormula = async (setDisableBtn, api, setLoader) => {
  setDisableBtn(true);
  setLoader(true);
  try {
    const response = await api();
    if (response && response.data) {
      if (response.data.validationPass || !response.data.errorResponse) {
        return response.data;
      } else if (
        response.data.errorResponse &&
        response.data.errorResponse.errorMessage != undefined &&
        response.data.errorResponse.errorType != undefined
      ) {
        const shortCode = response.data.errorResponse.errorCode.replace(/\d_\w+_\w+_/, '');
        MessageCenter.addNotification(
          response.data.errorResponse.errorMessage,
          response.data.errorResponse.errorType,
          shortCode
        );
        return response.data;
      }
    }
  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    setDisableBtn(false);
    setLoader(false);
  }
};
